import React from "react";
import Image from "next/image";
import { parseCookies } from "nookies";
import { useStoreState } from "pullstate";
import Link from "next/link";
import classnames from "classnames";

import Container from "@/components/Container";
import Typography from "@/components/Typography";
import PodcastSchedule from "@/components/PodcastSchedule";
import { applicationRoutes } from "@/helpers/applicationRouting";
import Tag from "@/components/Tag";
import {
  AuthorInterface,
  PlayerStatusTypes,
  PodcastType,
  SlugType,
  TagInterface,
} from "@/helpers/pageHelpers/Common/interface";
import Button from "@/components/Button";
import PlayIcon from "@/icons/rounded-play.svg";
import PauseIcon from "@/icons/rounded-pause.svg";
import FollowPodcastButton from "@/components/FollowPodcastButton";
import NotificationButton from "@/components/NotificationButton";
import { NotificationTypeEnum } from "@/components/NotificationButton/NotificationButton.interface";
import { UIStore } from "@/store/UIStore";
import useRequest from "@/helpers/useRequest";
import { apiPath } from "@/helpers/apiPath";
import { PlayerStore, psPauseAudio, psPlaySource } from "@/store/PlayerStore";
import { getImgixAbsoluteURL } from "@/helpers/getImgixAbsoluteURL";
import { getImgixURL } from "@/helpers/getImgixURL";
import Spinner from "@/components/Spinner";

import styles from "./PodcastCover.module.scss";

export const PodcastCover: React.FC<{
  title: string;
  description: string;
  cover: string;
  logo: string;
  artwork: string;
  tags: TagInterface[];
  hosts: AuthorInterface[];
  slug: SlugType;
  followed: boolean;
  podcastType: PodcastType;
  schedule?: {
    startTime: string;
    endTime: string;
  };
}> = ({
  title,
  description,
  cover,
  logo,
  artwork,
  tags,
  slug,
  followed,
  podcastType,
  schedule,
  hosts,
}) => {
  const coverImage = getImgixURL(cover);
  const artworkImage = getImgixURL(artwork);
  const coverBackground = getImgixAbsoluteURL(cover);
  const { data } = useRequest(apiPath.EPISODES({ slug, page: 1, perPage: 1 }), {
    revalidateOnMount: true,
  });
  const episode = data?.items[0];
  const { userLoggedIn } = useStoreState(UIStore, (store) => store);
  const isMobileApp = process.env.NEXT_PUBLIC_MOBILE_APP === "true";
  const isClientSide = typeof window !== "undefined";
  const isMobile = isMobileApp && isClientSide;
  const isNotificationVisible =
    isMobile &&
    window.RNisNotification &&
    userLoggedIn &&
    podcastType === PodcastType.Live;
  const cookies = parseCookies();
  const apiToken = cookies?.token;
  const audioURL = apiToken
    ? `${episode?.media?.url}?api_token=${apiToken}`
    : episode?.media?.url;

  const { currentSource, status } = useStoreState(PlayerStore, (store) => ({
    currentSource: store.currentSource,
    status: store.playerStatus,
  }));

  const isActivePlayer = currentSource === audioURL;
  const isPlaying = isActivePlayer && status === PlayerStatusTypes.Playing;
  const isLoading = status === PlayerStatusTypes.Loading && isActivePlayer;
  const isArchived = podcastType === PodcastType.Archived;

  const mediaMetaData = {
    title: episode?.title,
    artwork: episode?.images.feed.lgx2,
    subTitle: episode?.podcast.title,
    slug: episode?.mediaSlug,
    podcast: { slug: slug },
    tags: !!tags?.length && tags.map(({ title }) => title),
    authors: !!hosts?.length && hosts.map(({ name }) => name),
  };

  const play = () => {
    PlayerStore.update(psPlaySource(audioURL, mediaMetaData, 0));
  };

  const stop = () => {
    PlayerStore.update(psPauseAudio);
  };

  return (
    <div
      className={styles.wrapper}
      style={{
        backgroundImage: `url(${coverBackground}?blur=40)`,
      }}
    >
      <div className={styles.cover}>
        <div className={styles.image}>
          <Image
            src={`${coverImage}?blend-crop=bottom%2Ccenter&blend-align=bottom%2Ccenter&blend-fit=fill&blend-mode=multiply&blend64=aHR0cHM6Ly9uZXdvbmNlLmltZ2l4Lm5ldC91cGxvYWRzL2ltYWdlcy80NTQ2OC9PdmVybGF5X2RhcmtfM3gucG5n&fit=fill`}
            objectFit="cover"
            layout="fill"
            alt="cover"
          />
        </div>
        <Container className={styles.container}>
          <PodcastSchedule podcastType={podcastType} schedule={schedule} />

          <div className={styles.content}>
            <div className={styles.artwork}>
              <div className="hide-on-desktop">
                <Image
                  src={artworkImage}
                  objectFit="cover"
                  layout="fill"
                  className="hide-on-desktop"
                  alt="artwork"
                />
              </div>
              <div className="hide-on-mobile">
                <Image src={logo} objectFit="cover" layout="fill" alt="logo" />
              </div>
            </div>
            <Typography variant="h1" component="h1" className={styles.heading}>
              {title}
            </Typography>

            <Button
              variant="playerSecondary"
              className={styles.play}
              onClick={isPlaying ? stop : play}
            >
              {isLoading ? (
                <Spinner />
              ) : isPlaying ? (
                <>
                  <PauseIcon />
                  Zatrzymaj
                </>
              ) : (
                <>
                  <PlayIcon />
                  Najnowszy epizod
                </>
              )}
            </Button>

            {!isArchived && (
              <div className={styles.actions}>
                <FollowPodcastButton
                  slug={slug}
                  followed={followed}
                  className="hide-on-mobile"
                  variant="round"
                  size="medium"
                />

                <FollowPodcastButton
                  slug={slug}
                  followed={followed}
                  className="hide-on-desktop"
                  variant="withLabel"
                  size="medium"
                />

                {isNotificationVisible && (
                  <>
                    <NotificationButton
                      slug={slug as string}
                      type={NotificationTypeEnum.Podcast}
                      variant="round"
                      size="medium"
                      className={classnames(
                        styles.notification,
                        "hide-on-mobile"
                      )}
                    />
                    <NotificationButton
                      slug={slug as string}
                      type={NotificationTypeEnum.Podcast}
                      variant={"withLabel"}
                      size="medium"
                      className={classnames(
                        styles.notification,
                        "hide-on-desktop"
                      )}
                    />
                  </>
                )}
              </div>
            )}

            <Typography
              variant="medium"
              component="p"
              className={styles.description}
            >
              {description}
            </Typography>

            {tags && (
              <div className={styles.tags}>
                {tags.map(({ title, id, slug }) => (
                  <Link
                    key={id}
                    href={`${applicationRoutes.TAG}[slug]`}
                    as={applicationRoutes.TAG + slug}
                  >
                    <a className={styles.tag}>
                      <Tag title={title} key={id}>
                        {title}
                      </Tag>
                    </a>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </Container>
      </div>
    </div>
  );
};

export default PodcastCover;
